import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { tv } from "tailwind-variants";

import { PageButtons } from "./page-buttons";

const paginationControlsVariants = tv({
  base: "flex flex-row items-center gap-1 text-sm text-content-muted",
});

const stepButton = tv({
  base: [
    "flex cursor-pointer flex-row items-center gap-1 rounded-md px-2 py-1.5",
    "transition-colors duration-150 hover:bg-surface-hover hover:text-content",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:outline-none",
    "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-content-muted",
  ],
});

type PaginationControlsProps = {
  className?: string;
  currentPage: number;
  numPages: number;
  setPage: (p: number) => void;
};

export const PaginationControls = ({
  className,
  currentPage,
  numPages,
  setPage,
}: PaginationControlsProps) => {
  return (
    /*
      Previous / pages / Next stay one adjacent group. Spreading them across the
      full table width with justify-between flung the two steppers into opposite
      corners, far from the page numbers they operate on.
    */
    <nav aria-label="Pagination" className={paginationControlsVariants({ className })}>
      <button
        className={stepButton()}
        disabled={currentPage === 1 || numPages === 0}
        onClick={() => {
          return setPage(currentPage - 1);
        }}
        type="button"
      >
        <ChevronLeftIcon aria-hidden="true" className="size-4" />

        <span className="hidden sm:inline">Previous</span>

        <span className="sr-only sm:hidden">Previous page</span>
      </button>

      <PageButtons currentPage={currentPage} numPages={numPages} setPage={setPage} />

      <button
        className={stepButton()}
        disabled={currentPage === numPages || numPages === 0}
        onClick={() => {
          return setPage(currentPage + 1);
        }}
        type="button"
      >
        <span className="hidden sm:inline">Next</span>

        <span className="sr-only sm:hidden">Next page</span>

        <ChevronRightIcon aria-hidden="true" className="size-4" />
      </button>
    </nav>
  );
};
